import { useEffect, useState } from "react";
import { Sparkles, Loader2, Upload, RefreshCw, Trash2, Eye, Film, Image as ImageIcon, Wand2, Check, XCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { errorMessage } from "@/lib/errors";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminStudioTab — internal batch panel for the AristocratsJA team.
 * Lists every job across all users + lets the admin re-process, view,
 * and delete in bulk. Also surfaces the studio's overall throughput.
 */
export default function AdminStudioTab() {
  const [jobs, setJobs] = useState([]);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bulkPreset, setBulkPreset] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const loadJobs = async () => {
    setLoading(true);
    try {
      const r = await api.get("/studio/jobs");
      setJobs(r.data?.items || []);
    } catch (err) { toast.error(errorMessage(err, "Couldn't load jobs")); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    loadJobs();
    api.get("/studio/presets").then((r) => setPresets(r.data?.items || []));
    const id = setInterval(loadJobs, 8000);
    return () => clearInterval(id);
  }, []);

  const toggle = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const selectAll = () => {
    if (selectedIds.size === jobs.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(jobs.map((j) => j.id)));
  };

  const bulkReprocess = async () => {
    if (!bulkPreset) { toast.error("Pick a preset"); return; }
    if (selectedIds.size === 0) { toast.error("Select at least one job"); return; }
    if (!confirm(`Re-process ${selectedIds.size} job(s) with "${bulkPreset}"?`)) return;
    setBusy(true);
    try {
      let ok = 0;
      for (const id of selectedIds) {
        try {
          const form = new FormData();
          form.append("preset_id", bulkPreset);
          await api.post(`/studio/jobs/${id}/reprocess`, form);
          ok += 1;
        } catch { /* skip */ }
      }
      toast.success(`Queued ${ok}/${selectedIds.size}`);
      setSelectedIds(new Set());
      loadJobs();
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this job + storage?")) return;
    try {
      await api.delete(`/studio/jobs/${id}`);
      toast.success("Deleted");
      loadJobs();
    } catch (err) { toast.error(errorMessage(err, "Delete failed")); }
  };

  const counts = jobs.reduce((acc, j) => {
    acc[j.status] = (acc[j.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div data-testid="admin-studio-tab" className="space-y-6">
      <div>
        <p className="text-[10px] uppercase tracking-[0.25em] text-gold mb-1">Admin · AI Media Studio</p>
        <h2 className="font-heading text-2xl text-white flex items-center gap-2">
          <Sparkles className="text-gold" size={22} />
          Batch enhancement queue
        </h2>
        <p className="text-white/45 text-sm mt-1">
          Every studio job across every user. Re-process in bulk with any of the 15 cinematic presets.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <Tile label="Total" value={jobs.length} />
        <Tile label="Queued" value={counts.queued || 0} color="amber" />
        <Tile label="Processing" value={counts.processing || 0} color="gold" />
        <Tile label="Complete" value={counts.complete || 0} color="emerald" />
        <Tile label="Failed" value={counts.failed || 0} color="rose" />
      </div>

      {/* Bulk control */}
      <div className="flex flex-wrap items-center gap-2 p-3 border border-white/10 rounded-md bg-black/30">
        <button onClick={selectAll}
          data-testid="admin-studio-select-all"
          className="text-[10px] uppercase tracking-wider px-2.5 py-1.5 border border-white/15 text-white/70 hover:text-white rounded">
          {selectedIds.size === jobs.length && jobs.length > 0 ? "Clear" : "Select all"}
        </button>
        <select value={bulkPreset} onChange={(e) => setBulkPreset(e.target.value)}
          data-testid="admin-studio-bulk-preset"
          className="bg-black/40 border border-white/15 rounded px-2.5 py-1.5 text-[10px] text-white uppercase tracking-wider">
          <option value="">Bulk re-process as…</option>
          {presets.map((p) => (<option key={p.id} value={p.id}>{p.label}</option>))}
        </select>
        <button
          data-testid="admin-studio-bulk-run"
          onClick={bulkReprocess}
          disabled={busy || !bulkPreset || selectedIds.size === 0}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gold text-luxury-black text-[10px] uppercase tracking-wider rounded font-medium hover:bg-gold/90 disabled:opacity-40">
          {busy ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />}
          Run ({selectedIds.size})
        </button>
        <button onClick={loadJobs}
          data-testid="admin-studio-refresh"
          className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-white/15 text-white/70 hover:text-white text-[10px] uppercase tracking-wider rounded">
          <RefreshCw size={10} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
        <a href="/studio" target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-gold/40 text-gold hover:bg-gold/10 text-[10px] uppercase tracking-wider rounded">
          <Upload size={10} /> New upload
        </a>
      </div>

      {/* Job table */}
      <div className="border border-white/10 rounded-md overflow-hidden">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="text-left text-[9px] uppercase tracking-wider text-white/45 border-b border-white/10 bg-black/40">
              <th className="px-2 py-2 w-8"></th>
              <th className="px-2 py-2">File</th>
              <th className="px-2 py-2">Owner</th>
              <th className="px-2 py-2">Preset</th>
              <th className="px-2 py-2">Scene</th>
              <th className="px-2 py-2">Status</th>
              <th className="px-2 py-2">Time</th>
              <th className="px-2 py-2 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => (
              <tr key={j.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                <td className="px-2 py-2">
                  <input type="checkbox" checked={selectedIds.has(j.id)}
                    onChange={() => toggle(j.id)}
                    data-testid={`admin-studio-pick-${j.id}`}
                    className="accent-gold-500" />
                </td>
                <td className="px-2 py-2 text-white">
                  <div className="inline-flex items-center gap-1.5">
                    {j.kind === "video" ? <Film size={10} className="text-gold/70" /> : <ImageIcon size={10} className="text-gold/70" />}
                    <span className="truncate max-w-[180px] inline-block align-middle">{j.filename}</span>
                  </div>
                  <p className="text-[9px] text-white/35">{(j.size_bytes / (1024 * 1024)).toFixed(1)} MB</p>
                </td>
                <td className="px-2 py-2 text-white/70 truncate max-w-[140px]">{j.owner_email.replace("@studio.aristocrats.local", "")}</td>
                <td className="px-2 py-2 text-gold/80">{j.preset_id || "—"}</td>
                <td className="px-2 py-2 text-white/65">{j.analysis?.scene || "—"}</td>
                <td className="px-2 py-2">
                  <StatusBadge job={j} />
                </td>
                <td className="px-2 py-2 text-white/45">
                  {j.duration_ms ? `${j.duration_ms}ms` : "—"}
                </td>
                <td className="px-2 py-2 text-right space-x-1">
                  <a href={`/studio?j=${j.id}`} target="_blank" rel="noopener noreferrer"
                    data-testid={`admin-studio-view-${j.id}`}
                    title="Open in studio"
                    className="inline-flex p-1 text-white/45 hover:text-gold"><Eye size={11} /></a>
                  <button onClick={() => remove(j.id)}
                    data-testid={`admin-studio-del-${j.id}`}
                    className="inline-flex p-1 text-white/45 hover:text-rose-300"><Trash2 size={11} /></button>
                </td>
              </tr>
            ))}
            {jobs.length === 0 && !loading && (
              <tr><td colSpan={8} className="px-3 py-10 text-center text-white/40 text-[11px]">
                No studio jobs yet. <a href="/studio" className="text-gold underline">Upload a test photo →</a>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Tile({ label, value, color = "white" }) {
  const colorMap = {
    gold: "border-gold/40 bg-gold/[0.06] text-gold",
    emerald: "border-emerald-400/30 bg-emerald-500/[0.06] text-emerald-300",
    amber: "border-amber-400/30 bg-amber-500/[0.06] text-amber-300",
    rose: "border-rose-400/30 bg-rose-500/[0.06] text-rose-300",
    white: "border-white/10 bg-black/30 text-white",
  };
  return (
    <div className={`p-3 rounded-md border ${colorMap[color]}`}>
      <p className="text-[9px] uppercase tracking-wider opacity-65">{label}</p>
      <p className="text-2xl font-display mt-0.5">{value}</p>
    </div>
  );
}

function StatusBadge({ job }) {
  const s = job.status;
  if (s === "complete")
    return <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border border-emerald-400/40 text-emerald-300 inline-flex items-center gap-1"><Check size={9} /> {s}</span>;
  if (s === "failed")
    return <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border border-rose-400/40 text-rose-300 inline-flex items-center gap-1"><XCircle size={9} /> {s}</span>;
  if (s === "processing")
    return <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border border-gold/40 text-gold inline-flex items-center gap-1"><Loader2 size={9} className="animate-spin" /> {job.progress_pct || 0}%</span>;
  return <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border border-white/20 text-white/65">{s}</span>;
}
